import { createContext, ReactNode, useContext, useMemo, useRef } from 'react'
import { createHtmlPortalNode, HtmlPortalNode, InPortal } from 'react-reverse-portal'
import { EditorShell, EditorShellRef } from './shell'

export interface EditorProviderProps {
	children?: ReactNode
}

export const EditorContext = createContext<{
	editorPortalNode: HtmlPortalNode | null
	shellRef: React.RefObject<EditorShellRef | null> | null
}>({
	editorPortalNode: null,
	shellRef: null,
})

export function EditorProvider({ children }: EditorProviderProps) {
	// Create a stable portal node that persists across editor mode switches
	const portalNode = useMemo(() => createHtmlPortalNode(), [])
	const shellRef = useRef<EditorShellRef>(null)
	const value = useMemo(() => ({
		editorPortalNode: portalNode,
		shellRef,
	}), [portalNode])

	return (
		<EditorContext.Provider value={value}>
			{/* Default EditorShell instance that will be overridden by OutPortal */}
			<InPortal node={portalNode}>
				<EditorShell
					ref={shellRef}
					defaultPanelId="details"
					enabledPanelIds={[]}
					detailsPanel={<div>Default Details</div>}
					editPanel={<div>Default Edit</div>}
					reviewPanel={<div>Default Review</div>}
				/>
			</InPortal>
			{children}
		</EditorContext.Provider>
	)
}

export function useEditorContext() {
	const context = useContext(EditorContext)
	if (!context.editorPortalNode || !context.shellRef) {
		throw new Error('useEditorContext must be used within an EditorProvider')
	}
	return context
}
